// /src/App.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import SceneView from '@/ui/SceneView';
import PartBrowser from '@/ui/components/PartBrowser';
import PartInspector from '@/ui/components/PartInspector';
import ModelBrowser from '@/ui/components/ModelBrowser';
import FileMenu from '@/ui/components/FileMenu';
import Toasts from '@/ui/components/Toasts';
import { importLDraw, importInternalJson } from '@/engine/importLdraw';
import { exportToLDraw, exportToInternalJson } from '@/engine/exportLdraw';
import { useModelStore, toSnapshot, MAIN_SUBMODEL_ID } from '@/store/modelStore';
import { useThemeStore } from '@/store/themeStore';
import { loadLDrawColors } from '@/engine/ldrawColors';
import { LDRAW_BASE } from '@/engine/ldrawConfig';
import {
  IconCrosshair,
  IconGrid,
  IconHeight,
  IconHinge,
  IconLink,
  IconLayers,
  IconMagnet,
  IconMoon,
  IconRedo,
  IconRotate,
  IconSun,
  IconTrash,
  IconUndo,
} from '@/ui/icons';

function downloadText(filename: string, text: string, mime = 'text/plain') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function readTextFile(file: File) {
  return await file.text();
}

export default function App() {
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  
  const mode = useModelStore((s) => s.mode);
  const uiMode = useModelStore((s) => s.uiMode);
  const renderMode = useModelStore((s) => s.renderMode);
  const snap = useModelStore((s) => s.snap);
  const parts = useModelStore((s) => s.parts);
  const instances = useModelStore((s) => s.instances);
  const activeSubmodelId = useModelStore((s) => s.activeSubmodelId);
  const submodels = useModelStore((s) => s.submodels);
  const collision = useModelStore((s) => s.collision);
  const pendingPlacement = useModelStore((s) => s.pendingPlacement);
  const undoStack = useModelStore((s) => s.undoStack);
  const redoStack = useModelStore((s) => s.redoStack);
  const viewStep = useModelStore((s) => s.viewStep);
  const currentStep = useModelStore((s) => s.currentStep);
  const selectedId = useModelStore((s) => s.selectedId);

  const contextId = useMemo(() => activeSubmodelId ?? MAIN_SUBMODEL_ID, [activeSubmodelId]);
  const contextMaxStep = useMemo(() => {
    let max = 1;
    for (const p of parts) if ((p.submodelId ?? MAIN_SUBMODEL_ID) === contextId) max = Math.max(max, p.step ?? 1);
    for (const i of instances) if ((i.parentSubmodelId ?? MAIN_SUBMODEL_ID) === contextId) max = Math.max(max, i.step ?? 1);
    return max;
  }, [parts, instances, contextId]);
  const stepListMax = useMemo(() => Math.max(currentStep, contextMaxStep), [currentStep, contextMaxStep]);
  const viewShown = useMemo(() => Math.min(viewStep, stepListMax), [viewStep, stepListMax]);

  const setMode = useModelStore((s) => s.setMode);
  const setUiMode = useModelStore((s) => s.setUiMode);
  const setRenderMode = useModelStore((s) => s.setRenderMode);
  const setSnap = useModelStore((s) => s.setSnap);
  const newModel = useModelStore((s) => s.newModel);
  const removeSelected = useModelStore((s) => s.removeSelected);
  const undo = useModelStore((s) => s.undo);
  const redo = useModelStore((s) => s.redo);
  const replaceAll = useModelStore((s) => s.replaceAll);
  const pushToast = useModelStore((s) => s.pushToast);
  const setViewStep = useModelStore((s) => s.setViewStep);
  const setCurrentStep = useModelStore((s) => s.setCurrentStep);

  useEffect(() => {
    loadLDrawColors(LDRAW_BASE).catch(() => {});
  }, []);

  const importRef = useRef<HTMLInputElement | null>(null);
  const importJsonRef = useRef<HTMLInputElement | null>(null);
  const [leftPanel, setLeftPanel] = useState<'parts' | 'model'>('parts');

  const brandLogoSrc = useMemo(() => {
    const file =
      theme === 'dark'
        ? 'brand/brickerator-logo-dark-t.png'
        : 'brand/brickerator-logo-light-t.png';
    return `${import.meta.env.BASE_URL}${file}`;
  }, [theme]);

  const collisionLabel = useMemo(() => {
    if (collision === 'ok') return { kind: 'ok', text: 'No collisions detected' };
    if (collision === 'warn') return { kind: 'warn', text: 'Possible overlap' };
    return { kind: 'bad', text: 'Likely collision' };
  }, [collision]);

  const activeSubmodelName = useMemo(() => {
    if (!activeSubmodelId) return 'Main model';
    return submodels.find((sm) => sm.id === activeSubmodelId)?.name ?? activeSubmodelId;
  }, [activeSubmodelId, submodels]);

  useEffect(() => {
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      const tag = (el?.tagName ?? '').toLowerCase();
      return !!((el as any)?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select');
    };

    const bumpMatrix = (m: number[], dx = 20, dz = 20) => {
      const out = [...m];
      if (out.length >= 16) {
        out[12] = Number(out[12] ?? 0) + dx;
        out[14] = Number(out[14] ?? 0) + dz;
      }
      return out;
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (isTypingTarget(ev.target)) return;

      const st = useModelStore.getState();
      const mod = ev.metaKey || ev.ctrlKey;
      const key = ev.key.toLowerCase();

      if (ev.key === 'Escape' && st.pendingPlacement) {
        ev.preventDefault();
        st.setPendingPlacement(null);
        return;
      }

      // Undo/Redo
      if (mod && key === 'z') {
        ev.preventDefault();
        if (ev.shiftKey) st.redo();
        else st.undo();
        return;
      }
      if (mod && key === 'y') {
        ev.preventDefault();
        st.redo();
        return;
      }

      // Delete
      if ((ev.key === 'Delete' || ev.key === 'Backspace') && st.uiMode === 'edit') {
        ev.preventDefault();
        st.removeSelected();
        return;
      }

      // Duplicate
      if (mod && key === 'd' && st.uiMode === 'edit') {
        ev.preventDefault();
        const id = st.selectedId;
        if (!id) return;
        const p = st.parts.find((pp) => pp.id === id);
        const ins = st.instances.find((ii) => ii.id === id);
        if (p) {
          st.addPart({
            name: p.name,
            partFile: p.partFile,
            ldrawColor: p.ldrawColor,
            colorHex: p.colorHex,
            step: p.step,
            submodelId: p.submodelId,
            matrix: bumpMatrix(p.matrix),
          });
          st.pushToast({ title: 'Duplicated part', detail: p.name, kind: 'ok' });
        } else if (ins) {
          st.addInstance({
            submodelId: ins.submodelId,
            parentSubmodelId: ins.parentSubmodelId,
            step: ins.step,
            matrix: bumpMatrix(ins.matrix),
          });
          st.pushToast({ title: 'Duplicated instance', detail: ins.id, kind: 'ok' });
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const formatErr = (err: unknown) => (err instanceof Error ? err.message : String(err));

  const handleImportLDraw = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    // allow importing the same file twice in a row
    e.currentTarget.value = '';
    if (!file) return;

    try {
      const text = await readTextFile(file);
      const snapshot = importLDraw(text);
      replaceAll(snapshot, true);
      pushToast({ title: 'Imported LDraw', detail: file.name, kind: 'ok' });
    } catch (err) {
      pushToast({ title: 'Import failed', detail: formatErr(err), kind: 'bad' });
    }
  };

  const handleImportJson = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    // allow importing the same file twice in a row
    e.currentTarget.value = '';
    if (!file) return;

    try {
      const text = await readTextFile(file);
      const snapshot = importInternalJson(text);
      replaceAll(snapshot, true);
      pushToast({ title: 'Loaded JSON', detail: file.name, kind: 'ok' });
    } catch (err) {
      pushToast({ title: 'Load failed', detail: formatErr(err), kind: 'bad' });
    }
  };

  return (
    <div className="app">
      <div className="topbar">
        <div className="brand">
          <div className="brandLogo" title="Brickerator">
            <img
              src={brandLogoSrc}
              alt="Brickerator"
              draggable={false}
            />
          </div>
        </div>

        <div className="toolbar">
          {/* File */}
          <div className="toolbarGroup">
            <FileMenu
              canExport={(parts.length + instances.length) > 0}
              onNew={() => {
                newModel();
                pushToast({ title: 'New model', detail: 'Cleared the scene.' });
              }}
              onImportLDraw={() => importRef.current?.click()}
              onExportLDraw={() => {
                const snap = toSnapshot(useModelStore.getState());
                const out = exportToLDraw(snap, { title: 'Brickerator Export', includeMeta: true });
                downloadText('brickerator-export.mpd', out, 'text/plain');
                pushToast({ title: 'Exported LDraw', detail: 'Downloaded brickerator-export.mpd', kind: 'ok' });
              }}
              onSaveJson={() => {
                const out = exportToInternalJson(toSnapshot(useModelStore.getState()));
                downloadText('brickerator-model.json', out, 'application/json');
                pushToast({ title: 'Saved JSON', detail: 'Downloaded brickerator-model.json', kind: 'ok' });
              }}
              onLoadJson={() => importJsonRef.current?.click()}
            />
          </div>

          {/* Edit actions */}
          <div className="toolbarGroup">
            <button
              className="btn iconOnly danger"
              onClick={removeSelected}
              disabled={uiMode !== 'edit' || !selectedId}
              title={uiMode !== 'edit' ? 'Editing disabled (Steps mode)' : 'Delete selected (Del)'}
              aria-label="Delete"
              type="button"
            >
              <IconTrash className="ico" />
            </button>

            <button
              className="btn iconOnly"
              onClick={undo}
              disabled={uiMode !== 'edit' || undoStack.length === 0}
              title="Undo (Ctrl/Cmd+Z)"
              aria-label="Undo"
              type="button"
            >
              <IconUndo className="ico" />
            </button>

            <button
              className="btn iconOnly"
              onClick={redo}
              disabled={uiMode !== 'edit' || redoStack.length === 0}
              title="Redo (Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z)"
              aria-label="Redo"
              type="button"
            >
              <IconRedo className="ico" />
            </button>
          </div>

          <div className="toolbarDivider" />
        
          {/* Modes */}
          <div className="toolbarGroup">
            <div className="seg" title="Editor profile">
              <button
                className={clsx(mode === 'beginner' && 'active')}
                onClick={() => setMode('beginner')}
                title="Basic: rotation snaps on Y only"
                type="button"
              >
                Basic
              </button>
              <button
                className={clsx(mode === 'advanced' && 'active')}
                onClick={() => setMode('advanced')}
                title="Pro: rotation snapping on all axes (Technic/hinges-friendly)"
                type="button"
              >
                Pro
              </button>
            </div>
        
            <div className="seg" title="Workflow">
              <button
                className={clsx(uiMode === 'edit' && 'active')}
                onClick={() => setUiMode('edit')}
                title="Edit: move/rotate parts (editing enabled)"
                type="button"
              >
                Edit
              </button>
              <button
                className={clsx(uiMode === 'instructions' && 'active')}
                onClick={() => setUiMode('instructions')}
                title="Steps: instruction viewer (editing disabled)"
                type="button"
              >
                Steps
              </button>
            </div>
        
            <div className="seg" title="Render mode">
              <button
                className={clsx(renderMode === 'edit' && 'active')}
                onClick={() => setRenderMode('edit')}
                title="View: editor shading + outline"
                type="button"
              >
                View
              </button>
              <button
                className={clsx(renderMode === 'render' && 'active')}
                onClick={() => setRenderMode('render')}
                title="Render: prettier lighting/bloom"
                type="button"
              >
                Render
              </button>
            </div>
          </div>
        
          <div className="toolbarDivider" />
        
          {/* Snapping */}
          <div className="toolbarGroup">
            <button
              className={clsx('btn iconOnly toggle', snap.enabled && 'on')}
              onClick={() => setSnap({ enabled: !snap.enabled })}
              data-tip="Snap"
              aria-label="Snap"
              disabled={uiMode !== 'edit'}
              type="button"
            >
              <IconMagnet className="ico" />
            </button>
            <button
              className={clsx('btn iconOnly toggle', snap.gridSnap && 'on')}
              onClick={() => setSnap({ gridSnap: !snap.gridSnap })}
              data-tip="Grid snap"
              aria-label="Grid snap"
              disabled={uiMode !== 'edit' || !snap.enabled}
              type="button"
            >
              <IconGrid className="ico" />
            </button>
            <button
              className={clsx('btn iconOnly toggle', snap.heightSnap && 'on')}
              onClick={() => setSnap({ heightSnap: !snap.heightSnap })}
              data-tip="Height snap"
              aria-label="Height snap"
              disabled={uiMode !== 'edit' || !snap.enabled}
              type="button"
            >
              <IconHeight className="ico" />
            </button>
            <button
              className={clsx('btn iconOnly toggle', snap.rotSnap && 'on')}
              onClick={() => setSnap({ rotSnap: !snap.rotSnap })}
              data-tip="Rotation snap"
              aria-label="Rotation snap"
              disabled={uiMode !== 'edit' || !snap.enabled}
              type="button"
            >
              <IconRotate className="ico" />
            </button>
            <button
              className={clsx('btn iconOnly toggle', snap.connectorSnap && 'on')}
              onClick={() => setSnap({ connectorSnap: !snap.connectorSnap })}
              data-tip="Connector snap"
              aria-label="Connector snap"
              disabled={uiMode !== 'edit' || !snap.enabled}
              type="button"
            >
              <IconLink className="ico" />
            </button>
            <button
              className={clsx('btn iconOnly toggle', snap.multiConnector && 'on')}
              onClick={() => setSnap({ multiConnector: !snap.multiConnector })}
              data-tip="Multi-connector"
              aria-label="Multi-connector"
              disabled={uiMode !== 'edit' || !snap.enabled || !snap.connectorSnap}
              type="button"
            >
              <IconLayers className="ico" />
            </button>
            <button
              className={clsx('btn iconOnly toggle', snap.alignOnConnect && 'on')}
              onClick={() => setSnap({ alignOnConnect: !snap.alignOnConnect })}
              data-tip="Align on connect"
              aria-label="Align on connect"
              disabled={uiMode !== 'edit' || !snap.enabled || !snap.connectorSnap}
              type="button"
            >
              <IconCrosshair className="ico" />
            </button>
            <button
              className={clsx('btn iconOnly toggle', snap.hingeSolver && 'on')}
              onClick={() => setSnap({ hingeSolver: !snap.hingeSolver })}
              data-tip="Hinge solver"
              aria-label="Hinge solver"
              disabled={uiMode !== 'edit' || !snap.enabled || !snap.connectorSnap}
              type="button"
            >
              <IconHinge className="ico" />
            </button>
          </div>
        
          <div style={{ flex: 1 }} />
        
          {/* Panels */}
          <div className="toolbarGroup">
            <div className="seg" title="Left panel">
              <button
                className={clsx(leftPanel === 'parts' && 'active')}
                onClick={() => setLeftPanel('parts')}
                title="Parts browser"
                type="button"
              >
                Parts
              </button>
              <button
                className={clsx(leftPanel === 'model' && 'active')}
                onClick={() => setLeftPanel('model')}
                title="Model / steps / submodels"
                type="button"
              >
                Model
              </button>
            </div>
        
          </div>

          {/* Theme */}
          <div className="toolbarGroup">
            <button
              className="btn iconOnly"
              onClick={toggleTheme}
              data-tip={theme === 'dark' ? 'Light mode' : 'Dark mode'}
              aria-label="Toggle theme"
              type="button"
            >
              {theme === 'dark' ? <IconSun className="ico" /> : <IconMoon className="ico" />}
            </button>
          </div>
        
          {/* Hidden inputs (triggered by the Import/Load buttons above) */}
          <input
            ref={importRef}
            type="file"
            accept=".ldr,.mpd,.dat,.txt"
            style={{ display: 'none' }}
            onChange={handleImportLDraw}
          />
          <input
            ref={importJsonRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleImportJson}
          />
        </div>
      </div>

      <div className="content">
        <div className="panel">
          {leftPanel === 'parts' ? <PartBrowser /> : <ModelBrowser />}
        </div>

        <div className="scene">
          <SceneView />

          <div className="hud">
            <div className="pill">
              <span className={clsx('statusDot', collisionLabel.kind)} />
              <span>{collisionLabel.text}</span>
              <span className="mono">|</span>
              {uiMode === 'edit' ? (
                pendingPlacement ? (
                  <>
                    placing <span className="mono">{pendingPlacement.name}</span> · click scene to place ·{' '}
                    <span className="mono">Esc</span> cancel
                  </>
                ) : (
                  <>
                    drag part to move · <span className="mono">E</span> rotate axes · <span className="mono">W</span> move axes ·{' '}
                    <span className="mono">F</span> focus · <span className="mono">Del</span> delete ·{' '}
                    <span className="mono">Ctrl/Cmd+Z</span> undo
                  </>
                )
              ) : (
                <>
                  <span className="mono">Esc</span> clear · instruction mode
                </>
              )}
            </div>
            <div className="pill">
              View step <b className="mono">{viewShown}</b>
              <span className="mono">·</span>
              Current step <b className="mono">{currentStep}</b>
              <span className="mono">·</span>
              <span className="muted">{activeSubmodelName}</span>
            </div>
          </div>

          <Toasts />
        </div>

        <div className="panel right">
          <PartInspector />
        </div>
      </div>

      <div className="bottombar">
        <div className="kbd">
          Tip: In beginner mode, rotation snaps on Y only. In advanced mode, rotation snaps on all axes.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="muted">View step</span>
          <input
            type="range"
            min={1}
            max={Math.max(1, stepListMax)}
            value={Math.min(viewShown, Math.max(1, stepListMax))}
            onChange={(e) => setViewStep(Number(e.target.value))}
          />
          <span className="muted">Current step</span>
          <input
            type="number"
            min={1}
            value={currentStep}
            onChange={(e) => setCurrentStep(Number(e.target.value))}
            style={{ width: 80 }}
          />
          <button
            className="btn pill"
            onClick={() => {
              const snap = toSnapshot(useModelStore.getState());
              navigator.clipboard.writeText(JSON.stringify(snap));
              pushToast({ title: 'Copied snapshot', detail: 'Model snapshot copied to clipboard.', kind: 'ok' });
            }}
            title="Copy a compact snapshot (useful for debugging)"
          >
            Copy snapshot
          </button>
        </div>
      </div>
    </div>
  );
}
